import React, {createContext, FC, useCallback, useContext, useMemo, useRef, useState} from 'react';
import {PrescoreDraft, RefreshOnboardingOptions, WizardContextValue, WizardStep} from '../types';
import {ChildrenProps} from '../../../../../utils';
import {StepStatus} from '../../../../../api-client';
import type {PrescoreValidationResult} from '../../../../../api-client';

const INITIAL_PRESCORE_DRAFT: PrescoreDraft = {
  borrowerInn: '',
  debitorInns: [''],
};

const WizardContext = createContext<WizardContextValue | null>(null);

export const useWizardContext = (): WizardContextValue => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizardContext must be used within WizardProvider');
  }
  return context;
};

interface WizardProviderProps extends ChildrenProps {
  initialStep?: WizardStep;
}

export const WizardProvider: FC<WizardProviderProps> = ({ 
  children, 
  initialStep = WizardStep.START 
}) => {
  const [activeStep, setActiveStep] = useState<WizardStep>(initialStep);
  const [completedSteps, setCompletedSteps] = useState<WizardStep[]>([]);
  const [onboardingStepStatus, setOnboardingStepStatus] = useState<StepStatus | null>(null);
  const [backendCurrentStep, setBackendCurrentStep] = useState<WizardStep | null>(null);
  const [onboardingStepNote, setOnboardingStepNote] = useState<string | undefined>(undefined);
  const [prescoreDraft, setPrescoreDraft] = useState<PrescoreDraft>(INITIAL_PRESCORE_DRAFT);
  const [lastPrescoreResult, setLastPrescoreResult] = useState<PrescoreValidationResult | null>(null);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState<boolean>(false);
  const refreshCallbackRef = useRef<((options?: RefreshOnboardingOptions) => Promise<void>) | null>(null);

  const refreshOnboardingState = useCallback(async (options?: RefreshOnboardingOptions) => {
    if (refreshCallbackRef.current) {
      await refreshCallbackRef.current(options);
    }
  }, []);

  const setRefreshCallback = useCallback((callback: (options?: RefreshOnboardingOptions) => Promise<void>) => {
    refreshCallbackRef.current = callback;
  }, []);

  const goToNextStep = useCallback(() => {
    setActiveStep((current) => {
      const nextStep = current + 1;
      if (nextStep <= WizardStep.DOCUMENTS) {
        return nextStep as WizardStep;
      }
      return current;
    });
  }, []);

  const goToPreviousStep = useCallback(() => {
    setActiveStep((current) => {
      const prevStep = current - 1;
      if (prevStep >= WizardStep.START) {
        return prevStep as WizardStep;
      }
      return current;
    });
  }, []);

  const isStepCompleted = useCallback((step: WizardStep) => {
    const currentForStepper = backendCurrentStep ?? activeStep;
    if (onboardingStepStatus != null || backendCurrentStep != null) {
      return step < currentForStepper;
    }
    return completedSteps.includes(step);
  }, [completedSteps, onboardingStepStatus, activeStep, backendCurrentStep]);

  const markStepCompleted = useCallback((step: WizardStep) => {
    setCompletedSteps((prev) => {
      if (!prev.includes(step)) {
        return [...prev, step];
      }
      return prev;
    });
  }, []);

  const value = useMemo<WizardContextValue>(() => ({
    activeStep,
    setActiveStep,
    goToNextStep,
    goToPreviousStep,
    isStepCompleted,
    completedSteps,
    markStepCompleted,
    onboardingStepStatus,
    setOnboardingStepStatus,
    backendCurrentStep,
    setBackendCurrentStep,
    onboardingStepNote,
    setOnboardingStepNote,
    prescoreDraft,
    setPrescoreDraft,
    lastPrescoreResult,
    setLastPrescoreResult,
    isInitialDataLoaded,
    setIsInitialDataLoaded,
    refreshOnboardingState,
    setRefreshCallback,
  }), [activeStep, completedSteps, onboardingStepStatus, backendCurrentStep, onboardingStepNote, prescoreDraft, lastPrescoreResult, isInitialDataLoaded, goToNextStep, goToPreviousStep, isStepCompleted, markStepCompleted, refreshOnboardingState, setRefreshCallback]);

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};

export default WizardContext;

